import { Point2D as Point2DInterface, TrussEnvelope } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { InsertingTrussEnvelopeData, SnappingLevel } from '../../../../store/slices/applicationSlice';
import { EntitySnapTarget } from '../../../layout-snapping/EntitySnapTarget';
import { EntitySnapTargetSelectionSettings } from '../../../layout-snapping/settings';
import { TrussEnvelopeSnapper } from '../../../layout-snapping/truss-envelope-snapping/trussEnvelopeSnapper';
import { GeometricEcosystem } from '../../ecosystem/GeometricEcosystem';
import { TrussEnvelopeSnappingSettings } from '../types';
import { StageTwoTrussEnvelope } from './stage2';

type EnvelopeOrigin =
  | {
      type: 'parallel-line';
      snappingTarget: EntitySnapTarget;
      actualSettings: TrussEnvelopeSnappingSettings;
    }
  | { type: 'point-selection' }
  | { type: 'unknown' };

export interface TrussEnvelopeStageOneProps {
  ecosystem: GeometricEcosystem;
  origin?: EnvelopeOrigin;
}

/**
 * Truss envelopes come into this world as a simple line. Use `boundIntoSegmentAroundDefiningPoint` to move on to the
 *   next stage of calculation.
 */
export class StageOneTrussEnvelope {
  /**
   * The line that is being proposed as the "best" truss envelope location at or near the `definingPoint` given the
   *   configured `GeometricEcosystem`, insertion modes and snapping settings.
   */
  line: Line2D;

  /**
   * Explains how the proposed `line` was chosen. Note that any "actual settings" recorded here may not match the user's
   *   current configured settings, as they may have changed since the envelope was calculated or inferred settings may
   *   have been used.
   */
  origin: EnvelopeOrigin;

  /**
   * The point which was used to define this truss envelope. Saved for reference by later stages.
   */
  definingPoint: Point2D;

  /**
   * Our source for any layout-related data needed for envelope calculations. Saved here for use by later stages.
   */
  ecosystem: GeometricEcosystem;

  /**
   * You probably don't want to call this constructor directly. Use the static `.fromPoints` method or a
   *   `TrussEnvelopeSnapper` instead, depending on how the envelope is defined.
   */
  constructor(definingPoint: Point2D, line: Line2D, props: TrussEnvelopeStageOneProps) {
    this.definingPoint = definingPoint;
    this.line = line;
    this.origin = props.origin ?? { type: 'unknown' };
    this.ecosystem = props.ecosystem;
  }

  /**
   * Used to create a stage one truss envelope from a single point and a parallel line snapping mode.
   */
  static fromSnappedParallelLine(
    point: Point2D,

    envelopeSnappingSettings: TrussEnvelopeSnappingSettings,
    pointSnappingSettings: EntitySnapTargetSelectionSettings,
    snappingLevel: SnappingLevel,
    ecosystem: GeometricEcosystem,
  ) {
    const snapper = new TrussEnvelopeSnapper(envelopeSnappingSettings, pointSnappingSettings, snappingLevel, ecosystem);

    switch (envelopeSnappingSettings.behaviorMode) {
      case 'closest-target':
        return snapper.snapEnvelopeParallelToClosestTarget(point);

      case 'closest-result':
        return snapper.snapEnvelopeToClosestLineParallelToTargets(point);

      default:
        assertExhaustiveSwitch(envelopeSnappingSettings.behaviorMode);
    }
  }

  /**
   * Used to create a hypothetical stage one truss envelope for recalculating an already existing truss envelope.
   */
  static forExistingEnvelope(
    trussEnvelope: Pick<TrussEnvelope, 'leftPoint' | 'rightPoint'>,
    ecosystem: GeometricEcosystem,
  ) {
    return StageOneTrussEnvelope.fromPoints(trussEnvelope.leftPoint, trussEnvelope.rightPoint, {
      ecosystem,
    });
  }

  static fromPoints(
    pointA: Point2DInterface,
    pointB: Point2DInterface,

    props: TrussEnvelopeStageOneProps,
  ) {
    const parsedPointA = Point2D.fromObject(pointA);
    const parsedPointB = Point2D.fromObject(pointB);

    if (parsedPointA.equals(parsedPointB)) return null;

    const lineSegment = new LineSegment2D(parsedPointA, parsedPointB);
    const definitionLine = lineSegment.toLine();

    // There's a chance the two points don't form a valid line within the roof base. Check this here, since this will
    //   throw an exception in stage 2 if not prevented.
    if (!props.ecosystem.getRoofAreas().some((area) => area.hasSegmentIntersectionWithLine(definitionLine)))
      return null;

    const definingPoint = lineSegment.midpoint;

    return new StageOneTrussEnvelope(definingPoint, definitionLine, props);
  }

  /**
   * Progresses to the next stage of envelope calculation, where our definition line will turn into a line segment.
   */
  boundIntoSegmentAroundDefiningPoint(envelopeData: InsertingTrussEnvelopeData) {
    return StageTwoTrussEnvelope.fromStageOneEnvelopeAndThickness(this, envelopeData);
  }
}
